import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IPushNotificationsComponentProps,
	IUserSessionComponentProps,
	PushNotificationsViewModelKey,
	UserSessionViewModelKey,
} from '@AppModels/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import {
	asApiError,
	INotificationMethods,
	IOperationResultNoValue,
	IUserPreferences,
	NotifyOf,
	UserViewModel,
} from '@ViewModels';
import { css } from 'aphrodite';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { BrowserPushNotificationsViewModel } from '../../../../../viewmodels/PushNotifications';
import { error as errorColor } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { HelpTooltip } from '../../../HelpTooltip';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IUserSessionComponentProps,
		IPushNotificationsComponentProps,
		IErrorMessageComponentProps {
	userViewModel: UserViewModel;
	onCancel?: () => void;
}

interface IState {
	notifyOf: Record<NotifyOf, INotificationMethods>;
	toggleEmailPushNotifications: boolean;
	toggleTextingPushNotifications: boolean;
	toggleClickedEmailsPushNotifications: boolean;
	saving: boolean;
}

class _NotificationsSettings extends React.Component<IProps, IState> {
	private get pushNotificationsEnabled() {
		if (!BrowserPushNotificationsViewModel.isSupported) {
			return false;
		}

		const permissionDenied = !!window.Notification && window.Notification.permission === 'denied';
		return !permissionDenied;
	}

	private pushNotificationsChecked(type: NotifyOf) {
		if (!this.pushNotificationsEnabled) {
			return false;
		}

		const { pushNotifications, userViewModel } = this.props;
		const { toggleEmailPushNotifications, toggleTextingPushNotifications, toggleClickedEmailsPushNotifications } =
			this.state;
		const enabled =
			!!userViewModel.preferences && !!userViewModel.preferences.notifyOf && !!userViewModel.preferences.notifyOf[type]
				? userViewModel.preferences.notifyOf[type].pushNotifications
				: true;

		const checked = enabled && pushNotifications.isActive;

		if (type === NotifyOf.TextsReceived) {
			return toggleTextingPushNotifications ? !checked : checked;
		} else if (type === NotifyOf.ReadEmails) {
			return toggleEmailPushNotifications ? !checked : checked;
		} else if (type === NotifyOf.ClickedEmails) {
			return toggleClickedEmailsPushNotifications ? !checked : checked;
		}
	}

	constructor(props: IProps) {
		super(props);
		this.state = {
			notifyOf: this.getUserSessionNotifyOfSettings(),
			saving: false,
			toggleClickedEmailsPushNotifications: false,
			toggleEmailPushNotifications: false,
			toggleTextingPushNotifications: false,
		};
	}

	public render() {
		const { userSession } = this.props;
		const { saving } = this.state;
		const surveysFeatures = userSession?.account?.features?.surveys;
		return (
			<>
				<div className={css(styleSheet.title)}>Track with Levitate - notify when a recipient has read your email</div>
				<div className={css(styleSheet.body)}>{this.renderEmailCheckBoxes()}</div>
				<div className={css(styleSheet.title)}>
					Track with Levitate - notify when a recipient clicks a link in your email
				</div>
				<div className={css(styleSheet.body)}>{this.renderClickedEmailsCheckBoxes()}</div>
				<div className={css(styleSheet.title)}>Text with Levitate - notify when a new text message is received</div>
				<div className={css(styleSheet.body)}>{this.renderTextingCheckBoxes()}</div>
				<div className={css(styleSheet.title)}>
					Email Campaigns - notify about the progress of your group email sends
				</div>
				<div className={css(styleSheet.body)}>{this.renderGroupEmailProgressCheckBoxes()}</div>
				{surveysFeatures?.enabled && surveysFeatures?.newReviewsEmailNotification && userSession?.isAdmin ? (
					<>
						<div className={css(styleSheet.title)}>
							Reviews with Levitate - notify when a new Google review is received
						</div>
						<div className={css(styleSheet.body)}>{this.renderReviewsCheckBoxes()}</div>
					</>
				) : null}
				{userSession?.isAdmin ? (
					<>
						<div className={css(styleSheet.title)}>Notify when a user disconnects from Levitate</div>
						<div className={css(styleSheet.body)}>{this.renderUserDisconnectCheckBoxes()}</div>
					</>
				) : null}
				<div className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={this.onSaveButtonClicked} disabled={saving}>
						<span>Save</span>
					</button>
					<button
						className={css(baseStyleSheet.ctaButtonReverse)}
						onClick={this.onCancelButtonClicked}
						disabled={saving}
					>
						<span>Cancel</span>
					</button>
				</div>
			</>
		);
	}

	private renderEmailCheckBoxes() {
		const { notifyOf, saving } = this.state;

		if (saving || !notifyOf) {
			return <LoadingSpinner />;
		}

		return (
			<div className={css(styleSheet.checkboxes)}>
				<Checkbox
					checked={!!notifyOf.ReadEmails?.email}
					className={css(styleSheet.checkbox, styleSheet.emailCheckbox)}
					id='notifications-settings-option-email'
					key='email'
					onChange={this.onCheckboxChanged(NotifyOf.ReadEmails, 'email')}
				>
					<span>Notify me via email</span>
				</Checkbox>
				{this.renderBrowserPushOption(NotifyOf.ReadEmails)}
			</div>
		);
	}

	private renderClickedEmailsCheckBoxes() {
		const { notifyOf, saving } = this.state;

		if (saving || !notifyOf) {
			return <LoadingSpinner />;
		}

		return (
			<div className={css(styleSheet.checkboxes)}>
				<Checkbox
					checked={!!notifyOf.ClickedEmails?.email}
					className={css(styleSheet.checkbox, styleSheet.emailCheckbox)}
					id='notifications-settings-option-clicked-emails'
					key='email'
					onChange={this.onCheckboxChanged(NotifyOf.ClickedEmails, 'email')}
				>
					<span>Notify me via email</span>
				</Checkbox>
				{this.renderBrowserPushOption(NotifyOf.ClickedEmails)}
			</div>
		);
	}

	private renderTextingCheckBoxes() {
		const { notifyOf, saving } = this.state;

		if (saving) {
			return <LoadingSpinner />;
		}

		return (
			<div className={css(styleSheet.checkboxes)}>
				<Checkbox
					checked={!!notifyOf.TextsReceived?.email}
					className={css(styleSheet.checkbox, styleSheet.emailCheckbox)}
					id='notifications-settings-option-texting'
					key='email'
					onChange={this.onCheckboxChanged(NotifyOf.TextsReceived, 'email')}
				>
					<span>Notify me via email</span>
				</Checkbox>
				{this.renderBrowserPushOption(NotifyOf.TextsReceived)}
			</div>
		);
	}

	private renderGroupEmailProgressCheckBoxes() {
		const { notifyOf, saving } = this.state;

		if (saving) {
			return <LoadingSpinner />;
		}

		return (
			<div className={css(styleSheet.checkboxes)}>
				<Checkbox
					checked={!!notifyOf.GroupEmailProgress?.email}
					className={css(styleSheet.checkbox, styleSheet.emailCheckbox)}
					id='notifications-settings-option-group-email-progress'
					key='email'
					onChange={this.onCheckboxChanged(NotifyOf.GroupEmailProgress, 'email')}
				>
					<span>Notify me via email</span>
				</Checkbox>
			</div>
		);
	}

	private renderReviewsCheckBoxes() {
		const { notifyOf, saving } = this.state;

		if (saving) {
			return <LoadingSpinner />;
		}

		return (
			<div className={css(styleSheet.checkboxes)}>
				<Checkbox
					checked={!!notifyOf.GoogleReviewsReceived?.email}
					className={css(styleSheet.checkbox, styleSheet.emailCheckbox)}
					id='notifications-settings-option-reviews'
					key='email'
					onChange={this.onCheckboxChanged(NotifyOf.GoogleReviewsReceived, 'email')}
				>
					<span>Notify me via email</span>
				</Checkbox>
			</div>
		);
	}
	private renderUserDisconnectCheckBoxes() {
		const { notifyOf, saving } = this.state;

		if (saving) {
			return <LoadingSpinner />;
		}

		return (
			<div className={css(styleSheet.checkboxes)}>
				<Checkbox
					checked={!!notifyOf.UserDisconnect?.email}
					className={css(styleSheet.checkbox, styleSheet.emailCheckbox)}
					id='notifications-settings-option-user-disconnect'
					key='email'
					onChange={this.onCheckboxChanged(NotifyOf.UserDisconnect, 'email')}
				>
					<span>Notify me via email</span>
				</Checkbox>
			</div>
		);
	}

	private renderBrowserPushOption(type: NotifyOf) {
		if (!BrowserPushNotificationsViewModel.isSupported) {
			return null;
		}

		const permissionDenied = !!window.Notification && window.Notification.permission === 'denied';
		const enabled = !permissionDenied && this.pushNotificationsEnabled;
		const checked = this.pushNotificationsChecked(type);

		return (
			<div className={css(styleSheet.pushCheckboxContainer)}>
				<Checkbox
					checked={checked}
					className={css(styleSheet.checkbox)}
					disabled={!enabled}
					id={`notifications-settings-option-pushNotifications-${type}`}
					key={`pushNotifications-${type}`}
					onChange={this.onTogglePushNotifications(type)}
				>
					<span>Notify me via browser notifications</span>
				</Checkbox>
				{!!permissionDenied && (
					<HelpTooltip>
						<span className={css(styleSheet.pushCheckboxDeniedHelp)}>
							<span style={{ color: errorColor }}>Permission denied.</span>
							&nbsp;
							<span>You previously denied Levitate permission to send notifications to this browser.</span>
							<br />
							<span>To re-enable this option, grant Levitate permission from within your browser&apos;s settings.</span>
						</span>
					</HelpTooltip>
				)}
			</div>
		);
	}

	private onCancelButtonClicked = () => {
		this.setState({
			notifyOf: this.getUserSessionNotifyOfSettings(),
			toggleClickedEmailsPushNotifications: false,
			toggleEmailPushNotifications: false,
			toggleTextingPushNotifications: false,
		});
		this.props.onCancel?.();
	};

	private onTogglePushNotifications = (type: NotifyOf) => () => {
		const { toggleEmailPushNotifications, toggleTextingPushNotifications, toggleClickedEmailsPushNotifications } =
			this.state;
		if (type === NotifyOf.ReadEmails) {
			this.setState({
				toggleEmailPushNotifications: !toggleEmailPushNotifications,
			});
		} else if (type === NotifyOf.TextsReceived) {
			this.setState({
				toggleTextingPushNotifications: !toggleTextingPushNotifications,
			});
		} else if (type === NotifyOf.ClickedEmails) {
			this.setState({
				toggleClickedEmailsPushNotifications: !toggleClickedEmailsPushNotifications,
			});
		}
	};

	private onCheckboxChanged =
		(notifyOfType: NotifyOf, type: keyof INotificationMethods) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const { notifyOf } = this.state;
			const checked = !!e.target.checked;

			const nextNotifyOf = { ...notifyOf };
			nextNotifyOf[notifyOfType] = { ...nextNotifyOf[notifyOfType], [type]: checked };
			this.setState({ notifyOf: nextNotifyOf });
		};

	private onSaveButtonClicked = () => {
		this.setState(
			{
				saving: true,
			},
			async () => {
				await this.saveSettings();
			}
		);
	};

	private updateUserPrefs = async (preferences: IUserPreferences) => {
		const { notifyOf } = this.state;
		const { userViewModel, logApiError, logEvent, errorMessages } = this.props;
		const promise = userViewModel.updateUserPreferences(preferences);
		if (!promise) {
			return;
		}

		logEvent('UpdateUserPreferences', { notifyOf });
		await promise.catch((error: IOperationResultNoValue) => {
			logApiError('UpdateUserPreferences-Error', error);

			errorMessages.pushApiError(error);
			this.setState({
				notifyOf: this.getUserSessionNotifyOfSettings(),
			});
		});
		this.setState({
			saving: false,
			toggleClickedEmailsPushNotifications: false,
			toggleEmailPushNotifications: false,
			toggleTextingPushNotifications: false,
		});
	};

	private saveSettings = async () => {
		const { notifyOf } = this.state;
		const { pushNotifications, userViewModel, logApiError, logEvent, errorMessages } = this.props;
		const userPrefs = { ...userViewModel.preferences };
		const emailPushNotificationsChecked = this.pushNotificationsChecked(NotifyOf.ReadEmails);
		const textingPushNotificationsChecked = this.pushNotificationsChecked(NotifyOf.TextsReceived);
		const clickedEmailsPushNotificationsChecked = this.pushNotificationsChecked(NotifyOf.ClickedEmails);

		userPrefs.notifyOf = {
			...notifyOf,
		};

		if (!this.pushNotificationsEnabled) {
			await this.updateUserPrefs(userPrefs);
			return;
		}

		userPrefs.notifyOf[NotifyOf.ReadEmails].pushNotifications = emailPushNotificationsChecked;
		userPrefs.notifyOf[NotifyOf.TextsReceived].pushNotifications = textingPushNotificationsChecked;
		userPrefs.notifyOf[NotifyOf.ClickedEmails].pushNotifications = clickedEmailsPushNotificationsChecked;

		const promise = pushNotifications.toggleNotifications(
			emailPushNotificationsChecked || textingPushNotificationsChecked || clickedEmailsPushNotificationsChecked,
			userPrefs,
			userViewModel
		);
		if (promise) {
			await promise.catch(error => {
				const err = asApiError(error);

				logApiError('EnableBrowserPush-Error', err);
			});
		}

		// If no push notifications are on and the subscription is still active, then unsubscribe

		if (
			!!pushNotifications.isActive &&
			!emailPushNotificationsChecked &&
			!textingPushNotificationsChecked &&
			!clickedEmailsPushNotificationsChecked
		) {
			logEvent('DeactivatePushSubscription', {
				id: pushNotifications.subscription.id,
			});

			pushNotifications.deactivateSubscription()?.catch((error: IOperationResultNoValue) => {
				logApiError('DeactivatePushSubscription-Error', error);

				errorMessages.pushApiError(error);
				this.setState({
					notifyOf: this.getUserSessionNotifyOfSettings(),
				});
			});
		}

		await this.updateUserPrefs(userPrefs);
	};

	private getUserSessionNotifyOfSettings = () => toJS(this.props.userViewModel.preferences.notifyOf);
}

const NotificationsSettingsAsObserver = observer(_NotificationsSettings);
const NotificationsSettingsWithContext = inject(
	UserSessionViewModelKey,
	PushNotificationsViewModelKey,
	ErrorMessagesViewModelKey
)(NotificationsSettingsAsObserver);
export const NotificationsSettings = withEventLogging(NotificationsSettingsWithContext, 'NotificationsSettings');
